import { Group, Stack } from '@mantine/core';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useExportTasks } from '../../../api/tasks-client/tasks-client.hooks';
import type { MeasureDetail } from '../../../api/work-periods-client/work-periods-client.type';
import i18n from '../../../base-dictionary';
import { MeasureInfoBox } from '../../../components/measure-info-box/measure-info-box';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { baseWhite } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { CollapseAllButton } from '../../tasks-table/buttons';
import { GroupingSelect } from '../../tasks-table/grouping-select';
import { getGroups } from '../../tasks-table/tasks-table.helpers';
import { useCollapseState } from '../../tasks-table/tasks-table.hooks';
import { ToggleCollapseValue } from '../../tasks-table/tasks-table.type';
import type { Grouping, TaskRecord } from '../../tasks-table/tasks-table.type';
import { TaskDetail } from './task-detail';
import { TaskTable } from './task-table';

type Props = {
  tasks: TaskRecord[];
  title: string;
  onBack: () => void;
  measureDetail?: MeasureDetail;
};

export const TaskDetails = ({ tasks, title, onBack, measureDetail }: Props) => {
  const [grouping, setGrouping] = useState<Grouping | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskRecord | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const { mutateAsync: exportTasks } = useExportTasks();

  const groups = getGroups(grouping, tasks);
  const { collapseState, setCollapseState, expandAll, collapseAll } = useCollapseState(groups);

  const allGroupsExpanded = Object.keys(collapseState).every(
    (key) => !collapseState[key as keyof typeof collapseState],
  );

  const updateCollapseState = (value: ToggleCollapseValue) => {
    if (value === ToggleCollapseValue.EXPAND_ALL) {
      expandAll();
    } else {
      collapseAll();
    }
  };

  const handleExportTasks = useCallback(async () => {
    if (tasks.length === 0) {
      return;
    }

    try {
      setIsExporting(true);
      const taskIds = tasks.map((task) => task.id.toString());
      const blob = await exportTasks(taskIds);

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = `${i18n.t('common.work_unit').toLowerCase()}_details_${dayjs().format('YYYY-MM-DD')}.csv`;

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export tasks:', error);
    } finally {
      setIsExporting(false);
    }
  }, [tasks, exportTasks]);

  const content = (
    <Stack>
      <Group justify="space-between">
        <Group>
          <Button
            size="sm"
            radius="xl"
            variant="secondary"
            onClick={onBack}
            leftSection={<Icon name="arrow_back" color={baseWhite} size={16} />}
          >
            {i18n.t('common.actions.back')}
          </Button>
          <Text weight="bold" size="large">
            {title}
          </Text>
        </Group>
        <Group>
          <GroupingSelect grouping={grouping} setGrouping={setGrouping} />
          <Button
            size="sm"
            radius="xl"
            variant="secondary"
            leftSection={<Icon name="get_app" variant="filled" color={baseWhite} size={16} />}
            onClick={handleExportTasks}
            loading={isExporting}
            disabled={tasks.length === 0 || isExporting}
          >
            {i18n.t('common.actions.download')}
          </Button>
          {grouping && (
            <CollapseAllButton
              value={allGroupsExpanded ? ToggleCollapseValue.COLLAPSE_ALL : ToggleCollapseValue.EXPAND_ALL}
              onClick={updateCollapseState}
            />
          )}
        </Group>
      </Group>
      <TaskTable
        tasks={tasks}
        grouping={grouping}
        groups={groups}
        onTaskClick={setSelectedTask}
        selectedTaskId={selectedTask?.id}
        collapseState={collapseState}
        setCollapseState={setCollapseState}
      />
    </Stack>
  );
  return (
    <PageLayout
      controlBar={undefined}
      overview={
        measureDetail && {
          content: <MeasureInfoBox measure={measureDetail} />,
        }
      }
      mainSection={content}
      sideSection={selectedTask && <TaskDetail task={selectedTask} onClose={() => setSelectedTask(null)} />}
      showSideSection={!!selectedTask}
    />
  );
};
